import React, { createContext, useContext, useMemo, useState, useCallback } from 'react'
import { TTSService } from '../services/tts'
import { ApiService } from '../services/api'
import { loadTodayMessages, saveMessageAudio, getMessageFromToday } from '../services/chatStorage'

interface SpeakingContextValue {
  isSpeaking: boolean
  speakingMessageId?: string
  speakText: (text: string, messageId: string, opts?: { auto?: boolean }) => Promise<void>
  stop: () => void
}

const SpeakingContext = createContext<SpeakingContextValue | undefined>(undefined)

export const SpeakingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [speakingMessageId, setSpeakingMessageId] = useState<string | undefined>(undefined)

  const stop = useCallback(() => {
    TTSService.stopCurrent()
    setIsSpeaking(false)
    setSpeakingMessageId(undefined)
  }, [])

  const speakText = useCallback(async (text: string, messageId: string, opts?: { auto?: boolean }) => {
    const trimmed = text.trim()
    if (!trimmed) return
    if (isSpeaking && speakingMessageId === messageId) return
    if (isSpeaking && speakingMessageId && speakingMessageId !== messageId) {
      try { console.info('Speaking: stopping current before switching', { from: speakingMessageId, to: messageId }); TTSService.stopCurrent() } catch {}
    }
    setSpeakingMessageId(messageId)
    setIsSpeaking(true)
    try { console.info('Speaking: start', { messageId, auto: !!opts?.auto }) } catch {}
    try {
      const cachedMsg = getMessageFromToday(messageId)
      const audio = cachedMsg?.audio
      const fresh = audio && typeof audio.ts === 'number' && Date.now() - audio.ts <= 170000
      if (audio?.audioLink && fresh) {
        try { console.info('Speaking: playing fresh cached audio', { messageId, audioLink: audio.audioLink }) } catch {}
        await TTSService.playAudioFromUrl(audio.audioLink, { allowMutedFallback: !opts?.auto })
        return
      }
      if (!fresh && audio?.requestId) {
        try { console.info('Speaking: refreshing audio by requestId', { messageId, requestId: audio.requestId }) } catch {}
        const req = await TTSService.getRequest(audio.requestId)
        if (req && req.status === 1 && req.result?.audio_link) {
          const audioUrl = req.result.audio_link as string
          saveMessageAudio(messageId, { requestId: audio.requestId, audioLink: audioUrl, status: 'SUCCESS', ts: Date.now() })
          try { console.info('Speaking: playing refreshed audio', { messageId, audioLink: audioUrl }) } catch {}
          await TTSService.playAudioFromUrl(audioUrl, { allowMutedFallback: !opts?.auto })
          return
        }
      }
      try { console.info('Speaking: synthesizing audio', { messageId }) } catch {}
      const voiceCode = TTSService.getSelectedVoiceCode() || 'hn_female_ngochuyen_full_48k-fhg'
      const res = await ApiService.postBinaryOrJson('/api/tts/synthesize', { text: trimmed, voiceCode, format: 'mp3' })
      if (res instanceof Blob) {
        try { console.info('Speaking: received Blob audio') } catch {}
        saveMessageAudio(messageId, { ts: Date.now() })
        await TTSService.playAudioFromBlob(res, { allowMutedFallback: !opts?.auto })
        return
      }
      if ((res as any)?.audio_url) {
        const audioUrl = (res as any).audio_url as string
        saveMessageAudio(messageId, { audioLink: audioUrl, status: 'SUCCESS', ts: Date.now() })
        try { console.info('Speaking: playing audio_url', { messageId, audioLink: audioUrl }) } catch {}
        await TTSService.playAudioFromUrl(audioUrl, { allowMutedFallback: !opts?.auto })
        return
      }
      const requestId = (res as any)?.data?.request_id as string | undefined
      if (requestId) {
        try { console.info('Speaking: polling requestId', { messageId, requestId }) } catch {}
        let attempt = 0
        while (attempt < 3) {
          try { console.debug('Speaking: poll attempt', { attempt: attempt + 1 }) } catch {}
          const statusRes = await TTSService.pollTTSStatus(requestId, { timeoutMs: 30000, intervalMs: 1000 })
          saveMessageAudio(messageId, { requestId, status: statusRes.status, ts: Date.now() })
          if (statusRes.status === 'SUCCESS' && statusRes.audio_link) {
            saveMessageAudio(messageId, { requestId, audioLink: statusRes.audio_link, status: 'SUCCESS', ts: Date.now() })
            try { console.info('Speaking: playing polled audio', { messageId, audioLink: statusRes.audio_link }) } catch {}
            await TTSService.playAudioFromUrl(statusRes.audio_link, { allowMutedFallback: !opts?.auto })
            return
          }
          if (statusRes.status !== 'IN_PROGRESS') {
            try { console.error('Speaking: poll failed', { messageId, statusRes }) } catch {}
            throw new Error(statusRes.error_message || 'Yêu cầu TTS thất bại')
          }
          attempt++
        }
        return
      }
      throw new Error('Unexpected TTS response')
    } catch (err) {
      if (opts?.auto) {
        try { console.warn('Speaking: auto playback failed', err as any) } catch {}
        setIsSpeaking(false)
        setSpeakingMessageId(undefined)
        return
      }
      try { console.error('Speaking: playback error', err as any) } catch {}
      throw err as any
    } finally {
      try { console.info('Speaking: finished', { messageId }) } catch {}
      setIsSpeaking(false)
      setSpeakingMessageId(undefined)
    }
  }, [isSpeaking, speakingMessageId])

  const value = useMemo(() => ({ isSpeaking, speakingMessageId, speakText, stop }), [isSpeaking, speakingMessageId, speakText, stop])

  return <SpeakingContext.Provider value={value}>{children}</SpeakingContext.Provider>
}

export function useSpeaking(): SpeakingContextValue {
  const ctx = useContext(SpeakingContext)
  if (!ctx) throw new Error('SpeakingContext not found')
  return ctx
}
